import React, {useEffect, useMemo, useState} from "react";
import {Dialog} from "primereact/dialog";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {Tag} from "primereact/tag";
import {SelectButton} from "primereact/selectbutton";
import {Checkbox} from "primereact/checkbox";

import {AppContext} from "../../HomePage";
import {MarketplaceProductService} from "../../service/MarketplaceProductService";

// Dialog picker for mp_products (Task 18): browse the campaign's marketplace
// catalog and choose which products to track/reprice. Mirrors
// ShopifyProductDialog's conventions -- a modal Dialog, `t`/error handling
// pulled from AppContext rather than prop-drilled, reset-on-open -- but keeps
// a real PrimeReact DataTable with page-based pagination (page/has_next)
// instead of Shopify's cursor, since there is no per-variant nesting here.
//
// This is the exact search/table/track logic that used to live inline in
// MarketplaceProductsStep (always open); it now lives here so the step can
// show a compact summary panel instead, opening this dialog on demand.
export default function MarketplaceProductDialog({visible, onHide, campaignId, onChanged}) {
    const {t} = React.useContext(AppContext);
    const service = useMemo(() => new MarketplaceProductService(), []);
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [query, setQuery] = useState('');
    // Default to the untracked catalog so tracking a product makes it leave the
    // list (previously everything stayed visible with only a badge flip, which
    // read as "nothing happened"). 'tracked' lists what's already tracked;
    // 'all' is the old unfiltered view.
    const [statusFilter, setStatusFilter] = useState('untracked');
    // Orthogonal to the status tab: a marketplace catalog is mostly dead
    // listings (a merchant with ~900 live products can carry 5000+ entries), so
    // this narrows every tab -- and the header counts, and "select all N" -- to
    // products that currently have stock. On by default: the in-stock walk is a
    // fraction of the full one (1.4k vs 13k listings for one seller), and the
    // full walk is slow enough to time out.
    const [inStockOnly, setInStockOnly] = useState(true);
    const [selection, setSelection] = useState([]);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [error, setError] = useState(null);
    // Campaign-wide count of tracked products, shown in the header. Comes back
    // with every list response (result.tracked_total) so it stays fresh after
    // track/untrack, regardless of which tab/page is showing.
    const [trackedCount, setTrackedCount] = useState(null);
    // {tracked, deactivated, untracked} for the header, loaded asynchronously --
    // untracked walks the whole marketplace catalog so it lags the product list.
    const [counts, setCounts] = useState(null);
    // The counts request walks the whole catalog and can time out on a large
    // one; remember that so the header spinner stops instead of spinning forever.
    const [countsFailed, setCountsFailed] = useState(false);
    // "Select all N matching" (Gmail-style): the action applies to EVERY product
    // matching the current tab+search, across all pages -- not just the page.
    const [selectAllMatching, setSelectAllMatching] = useState(false);

    const load = (nextPage = page, nextQuery = query, nextStatus = statusFilter, nextInStock = inStockOnly) => {
        if (!campaignId) return;
        setLoading(true);
        service.listProducts(campaignId, nextPage, nextQuery, nextStatus, nextInStock)
            .then(result => {
                setItems(result.items); setPage(result.page);
                setHasNext(result.has_next); setSelection([]); setError(null);
                if (result.tracked_total != null) setTrackedCount(result.tracked_total);
            })
            .catch(err => setError(err?.response?.data?.error || t('api.detail.500')))
            .finally(() => setLoading(false));
    };

    const loadCounts = (nextInStock = inStockOnly) => {
        if (!campaignId) return;
        setCounts(null); setCountsFailed(false);
        service.getProductCounts(campaignId, nextInStock).then(setCounts).catch(() => setCountsFailed(true));
    };

    // Reset + (re)load every time the dialog opens, rather than once on
    // mount -- the dialog itself mounts once and opens/closes repeatedly.
    useEffect(() => {
        if (visible) { setQuery(''); setSelection([]); setFeedback(null); setError(null); setStatusFilter('untracked'); setInStockOnly(true); setTrackedCount(null); setCounts(null); setCountsFailed(false); setSelectAllMatching(false); load(0, '', 'untracked', true); loadCounts(true); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, campaignId]);

    // Explicit user navigation (new search / page change) drops any leftover
    // feedback from a previous track/untrack action.
    const search = () => { setFeedback(null); setSelectAllMatching(false); load(0, query); };
    const goToPage = (nextPage) => { setFeedback(null); setSelectAllMatching(false); load(nextPage, query); };
    // Switching tab reloads from page 0 with the new filter. PrimeReact's
    // SelectButton emits null when the active button is re-clicked; ignore that
    // so the tab can't be toggled off into an empty state.
    const changeFilter = (value) => {
        if (!value || value === statusFilter) return;
        setStatusFilter(value); setFeedback(null); setSelection([]); setSelectAllMatching(false); load(0, query, value);
    };
    // The in-stock toggle changes what "all matching" means, so the header
    // counts have to be recomputed alongside the list.
    const changeInStock = (value) => {
        setInStockOnly(value); setFeedback(null); setSelection([]); setSelectAllMatching(false);
        load(0, query, statusFilter, value); loadCounts(value);
    };

    const track = (activate) => {
        const done = (result) => {
            setFeedback(result);
            setSelectAllMatching(false);
            load();
            loadCounts();
            // Let the step refresh its tracked-count summary panel.
            if (onChanged) onChanged(result);
        };
        const fail = (err) => {
            const data = err?.response?.data;
            // The backend refuses a track request that would go past the plan's
            // (account-wide) product limit as a whole; say how many still fit.
            setError(data?.error_code === 'product_limit_exceeded'
                ? t('wizard.mpProducts.limitExceeded', data.details)
                : (data?.error || t('api.detail.500')));
            setLoading(false);
        };
        if (selectAllMatching) {
            // Direction follows the tab (untracked->track, tracked->untrack); the
            // backend infers it from `status`, so the `activate` arg isn't needed.
            setLoading(true);
            service.trackAllMatching(campaignId, query, statusFilter, inStockOnly).then(done).catch(fail);
            return;
        }
        const keys = selection.map(item => item.key);
        if (keys.length === 0) return;
        setLoading(true);
        service.updateTracking(campaignId, activate ? keys : [], activate ? [] : keys).then(done).catch(fail);
    };

    const imageTemplate = (rowData) => rowData.image_url
        ? <img referrerPolicy="no-referrer" className="img-fluid-thumb" src={rowData.image_url} alt=""/>
        : null;

    const titleTemplate = (rowData) => rowData.url
        ? <a href={rowData.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            {rowData.title}
        </a>
        : rowData.title;

    const statusTemplate = (rowData) => (
        <Tag value={t(`wizard.mpProducts.status.${rowData.status}`)}
             severity={rowData.status === 'tracked' ? 'success' : undefined}/>
    );

    // Summarize the last track/untrack result; skipped>0 gets its own
    // warning Message(s) below, split by cause (plan limit vs unresolved
    // key) so a limit-only warning isn't shown for a purely
    // key-resolution problem, and vice versa.
    const feedbackSummary = () => {
        if (!feedback) return '';
        const parts = [];
        if (feedback.activated > 0) parts.push(t('wizard.mpProducts.activatedFeedback', {number: feedback.activated}));
        if (feedback.deactivated > 0) parts.push(t('wizard.mpProducts.deactivatedFeedback', {number: feedback.deactivated}));
        return parts.join(' ');
    };
    const summary = feedbackSummary();
    // Fall back gracefully for an older cached bundle whose response has no
    // skipped_limit/skipped_unresolved breakdown yet: treat every skip as a
    // plan-limit skip, matching the previous (pre-split) behavior.
    const skippedLimit = feedback ? (feedback.skipped_limit ?? feedback.skipped) : 0;
    const skippedUnresolved = feedback ? (feedback.skipped_unresolved ?? 0) : 0;

    const filterOptions = [
        {label: t('wizard.mpProducts.filter.untracked', 'Untracked'), value: 'untracked'},
        {label: t('wizard.mpProducts.filter.tracked', 'Tracked'), value: 'tracked'},
        {label: t('wizard.mpProducts.filter.all', 'All'), value: 'all'},
    ];

    // In the Untracked tab, when nothing untracked remains -- all products are
    // already tracked, not merely an empty search or a mid-catalog page --
    // replace the table with a friendly "all tracked" notice instead of the
    // bare "no products found" empty state. Not while the in-stock filter is on:
    // an empty page there means "nothing untracked WITH STOCK", which the
    // unconditional "all your products are tracked" wording would misstate.
    const showAllTrackedNotice = statusFilter === 'untracked' && !loading && !inStockOnly
        && items.length === 0 && page === 0 && !hasNext && !query.trim() && trackedCount > 0;

    // Gmail-style "select all across pages": once the whole current page is
    // selected and more matching products exist beyond it, offer to select every
    // matching product. untracked/tracked tabs only (the 'all' tab mixes both
    // track directions). totalMatching comes from the async header counts, which
    // walk the whole catalog and can take minutes (or time out) on a large one --
    // so the offer must not wait for them: until they arrive, other pages
    // existing is enough, and the label just leaves the number out. The
    // select-all action itself never needed the count.
    const totalMatching = statusFilter === 'tracked' ? (counts && counts.tracked)
        : statusFilter === 'untracked' ? (counts && counts.untracked) : null;
    const moreMatching = totalMatching != null ? totalMatching > items.length : (hasNext || page > 0);
    const canSelectAll = statusFilter !== 'all' && items.length > 0
        && selection.length === items.length && moreMatching;
    const countSuffix = totalMatching != null ? '' : 'Unknown';

    const footer = (
        <div style={{display: 'flex', justifyContent: 'flex-end'}}>
            <Button label={t('actions.close')} className="p-button-text" onClick={onHide}/>
        </div>
    );

    // Track/untrack action(s) for the current selection, rendered BOTH above the
    // table (next to search) and below it. Which button shows follows the active
    // status filter -- untrack on Tracked, track on Untracked, both on All.
    const selectionActions = (
        <>
            {statusFilter !== 'untracked' &&
                <Button label={t('wizard.mpProducts.untrackSelected')} className="p-button-outlined p-button-danger"
                        onClick={() => track(false)} disabled={loading || selection.length === 0}/>}
            {statusFilter !== 'tracked' &&
                <Button label={t('wizard.mpProducts.trackSelected')}
                        onClick={() => track(true)} disabled={loading || selection.length === 0}/>}
        </>
    );

    return (
        <Dialog header={t('wizard.mpProducts.title', 'Choose products to track')}
                visible={visible} onHide={onHide} footer={footer} modal
                style={{width: '54em', maxWidth: '95vw'}}>
            {(counts || trackedCount != null) &&
                <div style={{marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
                             fontWeight: 600, color: '#111827'}}>
                    <i className="pi pi-box" style={{color: '#16a34a'}}/>
                    <span>{counts
                        ? t('wizard.mpProducts.countsSummary',
                             {tracked: counts.tracked, deactivated: counts.deactivated, untracked: counts.untracked})
                        : t('wizard.mpProducts.trackedTotal', {number: trackedCount})}</span>
                    {/* The counts follow the in-stock filter, so say so -- otherwise
                        the numbers just drop when the box is ticked. */}
                    {inStockOnly &&
                        <span style={{fontWeight: 400, color: '#6b7280'}}>
                            {t('wizard.mpProducts.inStockScope', '(in stock only)')}
                        </span>}
                    {!counts && !countsFailed && <i className="pi pi-spin pi-spinner" style={{fontSize: '.85rem', color: '#9ca3af'}}/>}
                </div>}
            {error && <div style={{marginBottom: 12}}><Message severity="error" text={error}/></div>}
            {skippedLimit > 0 &&
                <div style={{marginBottom: 12}}>
                    <Message severity="warn"
                             text={t('wizard.mpProducts.limitReached', {skipped: skippedLimit, limit: feedback.limit})}/>
                </div>}
            {skippedUnresolved > 0 &&
                <div style={{marginBottom: 12}}>
                    <Message severity="warn"
                             text={t('wizard.mpProducts.unresolved', {skipped: skippedUnresolved})}/>
                </div>}
            {summary &&
                <div style={{marginBottom: 12}}><Message severity="success" text={summary}/></div>}

            <div style={{marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap'}}>
                <SelectButton value={statusFilter} options={filterOptions} disabled={loading}
                              onChange={(e) => changeFilter(e.value)}/>
                <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <Checkbox inputId="mpInStockOnly" checked={inStockOnly} disabled={loading}
                              onChange={(e) => changeInStock(!!e.checked)}/>
                    <label htmlFor="mpInStockOnly" style={{margin: 0, cursor: 'pointer'}}>
                        {t('wizard.mpProducts.filter.inStock', 'In stock only')}
                    </label>
                </span>
            </div>

            {showAllTrackedNotice &&
                <div style={{textAlign: 'center', padding: '2.5em 1em'}}>
                    <i className="pi pi-check-circle" style={{fontSize: '2rem', color: '#16a34a'}}/>
                    <div style={{marginTop: 12, fontWeight: 600, color: '#111827'}}>
                        {t('wizard.mpProducts.allTracked', 'All of your products are already tracked.')}
                    </div>
                </div>}

            {!showAllTrackedNotice && <>
            <div style={{display: 'flex', gap: 8, marginBottom: 12}}>
                <InputText value={query} onChange={(e) => setQuery(e.target.value)}
                           onKeyDown={(e) => e.key === 'Enter' && search()}
                           placeholder={t('wizard.mpProducts.search')} style={{flex: 1}}/>
                <Button icon="pi pi-search" onClick={search} disabled={loading}/>
                {selectionActions}
            </div>

            {canSelectAll &&
                <div style={{marginBottom: 12, padding: '8px 12px', background: '#eff6ff',
                             border: '1px solid #bfdbfe', borderRadius: 8, display: 'flex',
                             alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: '.85rem'}}>
                    {selectAllMatching
                        ? <>
                            <span>{t('wizard.mpProducts.allMatchingSelected' + countSuffix, {number: totalMatching})}</span>
                            <Button label={t('wizard.mpProducts.clearSelection')} className="p-button-link"
                                    style={{padding: 0, fontSize: '.85rem'}} onClick={() => setSelectAllMatching(false)}/>
                          </>
                        : <>
                            <span>{t('wizard.mpProducts.pageSelected', {number: selection.length})}</span>
                            <Button className="p-button-link" style={{padding: 0, fontSize: '.85rem'}}
                                    onClick={() => setSelectAllMatching(true)}
                                    label={t((statusFilter === 'tracked'
                                        ? 'wizard.mpProducts.selectAllTracked' : 'wizard.mpProducts.selectAllUntracked')
                                        + countSuffix, {number: totalMatching})}/>
                          </>}
                </div>}

            <DataTable value={items} loading={loading} dataKey="key" responsiveLayout="scroll"
                       selectionMode="multiple" metaKeySelection={false}
                       selection={selection} onSelectionChange={(e) => { setSelection(e.value); setSelectAllMatching(false); }}
                       emptyMessage={t('wizard.mpProducts.empty')}>
                <Column selectionMode="multiple" headerStyle={{width: '3em'}}/>
                <Column body={imageTemplate} header={t('wizard.mpProducts.table.image')} headerStyle={{width: '6em'}}/>
                <Column body={titleTemplate} field="title" header={t('wizard.mpProducts.table.title')}/>
                <Column field="key" header={t('wizard.mpProducts.table.key')}/>
                <Column field="price" header={t('wizard.mpProducts.table.price')}
                        headerStyle={{textAlign: 'right'}} style={{textAlign: 'right'}}/>
                <Column field="stock" header={t('wizard.mpProducts.table.stock')}
                        headerStyle={{textAlign: 'right'}} style={{textAlign: 'right'}}/>
                <Column body={statusTemplate} header={t('wizard.mpProducts.table.status')}/>
            </DataTable>

            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12}}>
                <div style={{display: 'flex', gap: 8}}>
                    <Button label={t('actions.previous')} icon="pi pi-chevron-left" className="p-button-text"
                            onClick={() => goToPage(page - 1)} disabled={loading || page <= 0}/>
                    <Button label={t('actions.next')} icon="pi pi-chevron-right" iconPos="right" className="p-button-text"
                            onClick={() => goToPage(page + 1)} disabled={loading || !hasNext}/>
                </div>
                <div style={{display: 'flex', gap: 8}}>
                    {selectionActions}
                </div>
            </div>
            </>}
        </Dialog>
    );
}
